import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { format, addDays, startOfWeek, subWeeks, addWeeks } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Trash2, Copy, Save, Send, Sparkles, History, AlertCircle, XCircle, RefreshCw, ChevronDown, ChevronUp, Info, RotateCcw, Lock, Lightbulb, Split, Star, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ProjectSelector } from './ProjectSelector';
import { CostCentreSelector } from './CostCentreSelector';
import { RoleSelector } from './RoleSelector';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTour } from '@/contexts/TourContext';
import { usePermissions } from '@/contexts/PermissionContext';
import type { TimesheetEntry } from '@/types/timesheet';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi, type PaginatedProjectsResponse, type PaginationInfo } from '@/services/timesheetApi';
import type { Project } from '@/types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { aiService } from '@/services/aiService';
import { rejectionService } from '@/services/rejectionService';
import { getOrganisationTimesheetSettings, getCachedLiveOrgSettings } from '@/services/settingsRepository';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { AnomalyAlertsPanelEnhanced } from './AIAssistant/AnomalyAlertsPanelEnhanced';
import { AIInsightsSheet } from './AIAssistant/AIInsightsSheet';
import { SmartAutoFillDialog } from './AIAssistant/SmartAutoFillDialog';
import { AuditHistoryDialog } from './AuditHistory';
import { BookingHoverCard, BookingActionMenu, ManualEntryActionMenu, BookingInlineActions, ManualEntryInlineActions, BookingDetailsModal, DismissBookingModal } from './ScheduledBookings';
import { AbsenceDetailsModal, AbsenceInlineActions } from './Absences';
import { autoLoadScheduledBookings, autoLoadScheduledBookingsAsync, hasEditedHours, dismissBooking, restoreBooking } from '@/services/scheduledBookingService';
import { SplitRowSheet } from './SplitRowSheet';
import type { StatusDotType } from './ScheduledBookings/StatusDot';
import { AbsenceIndicator, ReadOnlyIndicator } from './AbsenceBadge';
import type { AbsenceType } from './AbsenceBadge';
import { PageHeader } from '@/components/blocks/page-header';

const ALL_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const ALL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildDaysFromStart(weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
  const days: typeof ALL_DAYS[number][] = [];
  const labels: string[] = [];
  for (let i = 0; i < 7; i++) {
    const idx = (weekStartDay + i) % 7;
    days.push(ALL_DAYS[idx]);
    labels.push(ALL_DAY_LABELS[idx]);
  }
  return { days, labels };
}

function isWeekendDay(day: typeof ALL_DAYS[number]): boolean {
  return day === 'saturday' || day === 'sunday';
}

// Navigation state types for drilldown from reports
type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

interface TimesheetGridProps {
  selection?: TimesheetDrilldownState;
}

export function TimesheetGrid({ selection }: TimesheetGridProps = {}) {
  const location = useLocation();
  const { 
    projects, 
    costCentres, 
    currentEmployee, 
    employees,
    timesheets, 
    updateTimesheet,
    updateCostCentreLastUsed
  } = useTimesheetForScreen('timesheet');
  const { getScreenMode } = useDataSource();
  const { startPendingTour } = useTour();
  const { canUseAiAssistant, canDeleteTimesheet } = usePermissions();
  
  // Get current screen mode for workflow integration
  const screenMode = getScreenMode('timesheet');
  
  // Organization settings — loaded early so weekStartDay can drive day order
  const [organizationSettings, setOrganizationSettings] = useState<OrganisationTimesheetSettings | undefined>(() => {
    if (screenMode === 'live') {
      return getCachedLiveOrgSettings() || undefined;
    }
    return undefined;
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getOrganisationTimesheetSettings(screenMode);
        setOrganizationSettings(settings);
      } catch (error) {
        console.error('Failed to fetch organization settings:', error);
      }
    };
    fetchSettings();
  }, [screenMode]);

  // Get navigation state from either props or location state
  const navigationState = selection || (location.state as { timesheetDrilldown?: TimesheetDrilldownState })?.timesheetDrilldown;
  
  // Determine which employee's timesheet to display
  // Employee.id is now a number type matching the backend INT type
  const targetEmployeeId = navigationState?.employeeId ?? currentEmployee.id;
  
  const weekStartDay = organizationSettings?.weekStartDay ?? 1;
  const { days: DAYS, labels: DAY_LABELS } = useMemo(
    () => buildDaysFromStart(weekStartDay),
    [weekStartDay]
  );


  // Initialize week based on selection or default to current week
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    navigationState?.weekEnding
      ? startOfWeek(navigationState.weekEnding, { weekStartsOn: weekStartDay })
      : startOfWeek(new Date(), { weekStartsOn: weekStartDay })
  );

  // Check for pending tours when component mounts
  useEffect(() => {
    startPendingTour();
  }, [startPendingTour]);

  // Update current week when selection changes (e.g., navigating from Action Required card or Submission Consistency)
  useEffect(() => {
    if (navigationState?.weekEnding) {
      const newWeekStart = startOfWeek(navigationState.weekEnding, { weekStartsOn: weekStartDay });
      setCurrentWeekStart(newWeekStart);
    }
  }, [navigationState?.weekEnding]);

  // Get or create timesheet for current week
  const currentTimesheet = useMemo(() => {
    const weekEnding = addDays(currentWeekStart, 6);
    
    const existing = timesheets.find(
      ts => ts.employeeId === targetEmployeeId && 
      format(ts.weekEnding, 'yyyy-MM-dd') === format(weekEnding, 'yyyy-MM-dd')
    );
    
    if (existing) {
      return existing;
    }
    
    // Fallback: only create new timesheets for the actual current user
    return {
      id: `ts-new-${Date.now()}`,
      employeeId: currentEmployee.id,
      employeeName: currentEmployee.name,
      weekEnding,
      status: 'draft' as const,
      entries: []
    };
  }, [currentWeekStart, timesheets, targetEmployeeId, currentEmployee]);

    const [entries, setEntries] = useState<TimesheetEntry[]>(currentTimesheet.entries);
    const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
    const [showFillDescriptionDialog, setShowFillDescriptionDialog] = useState(false);
    const [bulkDescription, setBulkDescription] = useState('');
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [showAIInsightsSheet, setShowAIInsightsSheet] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(new Set());
  
  // Split row state
  const [showSplitRowSheet, setShowSplitRowSheet] = useState(false);
  const [entryToSplit, setEntryToSplit] = useState<TimesheetEntry | null>(null);
  
  // Scheduled bookings state
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<TimesheetEntry | null>(null);
  const [selectedBookingForDismiss, setSelectedBookingForDismiss] = useState<TimesheetEntry | null>(null);
  const [hasLoadedScheduledBookings, setHasLoadedScheduledBookings] = useState(false);
  
  // Absence state
  const [selectedAbsenceForDetails, setSelectedAbsenceForDetails] = useState<TimesheetEntry | null>(null);

  // Track editing state for hours inputs to prevent premature autocomplete
  const [editingHours, setEditingHours] = useState<Record<string, string | undefined>>({});
  const getHoursCellKey = (entryId: number, day: typeof DAYS[number]) => `${entryId}-${day}`;

  // Track cost centres per project code for filtering
  // Key: project code, Value: array of cost centres for that project
  const [costCentresByProjectCode, setCostCentresByProjectCode] = useState<Record<string, typeof costCentres>>({});
  const [loadingCostCentresForProject, setLoadingCostCentresForProject] = useState<Record<string, boolean>>({});

  // Paginated projects state for server-side pagination
  const PAGE_SIZE = 20;
  const [paginatedProjects, setPaginatedProjects] = useState<Project[]>([]);
  const [projectsPage, setProjectsPage] = useState(1);
  const [projectsTotalCount, setProjectsTotalCount] = useState(0);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsHasMore, setProjectsHasMore] = useState(true);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  // Projects for existing entries that may not be in the paginated list
  const [existingEntryProjects, setExistingEntryProjects] = useState<Project[]>([]);

  // Function to fetch cost centres filtered by project code
  const fetchCostCentresForProject = async (projectCode: string) => {
    // Skip if already loaded or currently loading
    if (costCentresByProjectCode[projectCode] || loadingCostCentresForProject[projectCode]) {
      return;
    }

    // In demo mode, use all cost centres (no filtering)
    if (screenMode === 'demo') {
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: costCentres
      }));
      return;
    }

    // In live mode, fetch filtered cost centres from the API
    setLoadingCostCentresForProject(prev => ({ ...prev, [projectCode]: true }));
    try {
      const filteredCostCentres = await timesheetApi.getCostCentresByProjectCode(projectCode);
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: filteredCostCentres
      }));
    } catch (error) {
      console.error(`Failed to fetch cost centres for project ${projectCode}:`, error);
      // Fall back to all cost centres on error
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: costCentres
      }));
    } finally {
      setLoadingCostCentresForProject(prev => ({ ...prev, [projectCode]: false }));
    }
  };

  // Load initial page of projects when component mounts (live mode only)
  const loadInitialProjects = React.useCallback(async () => {
    if (screenMode === 'demo') {
      // In demo mode, use all projects from context
      return;
    }
    
    setProjectsLoading(true);
    try {
      const response = await timesheetApi.getProjects({ 
        page: 1, 
        pageSize: PAGE_SIZE, 
        status: 'active' 
      });
      setPaginatedProjects(response.data);
      setProjectsTotalCount(response.pagination.totalItems);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsPage(1);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [screenMode]);

  // Load more projects (pagination)
  const loadMoreProjects = React.useCallback(async () => {
    if (projectsLoading || !projectsHasMore || screenMode === 'demo') return;
    
    setProjectsLoading(true);
    try {
      const nextPage = projectsPage + 1;
      const response = await timesheetApi.getProjects({ 
        page: nextPage, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        search: projectsSearchQuery || undefined
      });
      setPaginatedProjects(prev => [...prev, ...response.data]);
      setProjectsPage(nextPage);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsTotalCount(response.pagination.totalItems);
    } catch (error) {
      console.error('Failed to load more projects:', error);
      toast.error('Failed to load more projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [projectsLoading, projectsHasMore, projectsPage, projectsSearchQuery, screenMode]);

  // Handle server-side search for projects
  const handleProjectSearch = React.useCallback(async (query: string) => {
    if (screenMode === 'demo') return;
    
    setProjectsSearchQuery(query);
    setIsSearchingProjects(true);
    
    try {
      const response = await timesheetApi.getProjects({ 
        page: 1, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        search: query || undefined
      });
      setPaginatedProjects(response.data);
      setProjectsTotalCount(response.pagination.totalItems);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsPage(1);
    } catch (error) {
      console.error('Failed to search projects:', error);
    } finally {
      setIsSearchingProjects(false);
    }
  }, [screenMode]);

  // Fetch project details for existing entries that may not be in the paginated list
  const fetchExistingEntryProjects = React.useCallback(async () => {
    if (screenMode === 'demo') return;
    
    // Get unique project IDs from existing entries
    const existingProjectIds = new Set(
      entries
        .filter(e => e.projectId)
        .map(e => e.projectId)
    );
    
    // Find which project IDs are not in the paginated list
    const paginatedProjectIds = new Set(paginatedProjects.map(p => p.id));
    const missingProjectIds = Array.from(existingProjectIds).filter(
      id => id && !paginatedProjectIds.has(id)
    );
    
    if (missingProjectIds.length === 0) {
      setExistingEntryProjects([]);
      return;
    }
    
    // Fetch each missing project individually (Option C from the plan)
    const fetchedProjects: Project[] = [];
    for (const id of missingProjectIds) {
      if (id) {
        const project = await timesheetApi.getProjectById(id);
        if (project) {
          fetchedProjects.push(project);
        }
      }
    }
    setExistingEntryProjects(fetchedProjects);
  }, [entries, paginatedProjects, screenMode]);

  // Load initial projects on mount
  useEffect(() => {
    loadInitialProjects();
  }, [loadInitialProjects]);

  // Fetch existing entry projects when entries or paginated projects change
  useEffect(() => {
    fetchExistingEntryProjects();
  }, [fetchExistingEntryProjects]);

  // Combine paginated projects with existing entry projects for the selector
  const allAvailableProjects = useMemo(() => {
    let result: Project[];
    if (screenMode === 'demo') {
      result = projects;
    } else {
      const combined = [...paginatedProjects];
      existingEntryProjects.forEach(p => {
        if (!combined.some(cp => cp.id === p.id)) {
          combined.push(p);
        }
      });
      result = combined;
    }

    const showArchived = organizationSettings?.showArchivedProjects ?? false;
    if (!showArchived) {
      result = result.filter(p => p.active !== false);
    }

    const scope = organizationSettings?.projectSelectionScope ?? 'all';
    if (scope === 'assigned') {
      const usedProjectIds = new Set(entries.map(e => e.projectId).filter(Boolean));
      result = result.filter(p => p.isFavourite || usedProjectIds.has(p.id));
    }

    return result;
  }, [screenMode, projects, paginatedProjects, existingEntryProjects, organizationSettings?.showArchivedProjects, organizationSettings?.projectSelectionScope, entries]);

  // Calculate remaining count for "Load More" button
  const projectsRemainingCount = useMemo(() => {
    return Math.max(0, projectsTotalCount - paginatedProjects.length);
  }, [projectsTotalCount, paginatedProjects.length]);

  // Fetch cost centres for existing entries when entries change
  // This ensures cost centres are available for entries that already have a project selected
  useEffect(() => {
    const projectCodes = new Set<string>();
    entries.forEach(entry => {
      if (entry.projectId) {
        // Use allAvailableProjects to find the project (includes paginated + existing entry projects)
        const project = allAvailableProjects.find(p => p.id === entry.projectId);
        if (project?.code && !costCentresByProjectCode[project.code]) {
          projectCodes.add(project.code);
        }
      }
    });
    // Fetch cost centres for all unique project codes
    projectCodes.forEach(code => fetchCostCentresForProject(code));
  }, [entries, allAvailableProjects, costCentresByProjectCode, fetchCostCentresForProject]);

  // Sync entries when timesheet changes(id or entries from context)
  // We need to sync when:
  // 1. The timesheet ID changes (navigating to different week)
  // 2. The entries in the context change (after submission, the backend returns updated timesheet)
  React.useEffect(() => {
    console.log('📋 Syncing entries from timesheet:', currentTimesheet.id, 'Entries count:', currentTimesheet.entries.length);
    setEntries(currentTimesheet.entries);
    setHasLoadedScheduledBookings(false); // Reset flag when timesheet changes
  }, [currentTimesheet.id, currentTimesheet.entries]);

  // Monitor entries changes for debugging
  React.useEffect(() => {
    console.log('📊 Entries state updated. Total entries:', entries.length);
    if (entries.length > 0) {
      console.log('📝 Current entries:', entries.map(e => ({
        id: e.id,
        projectId: e.projectId,
        hasProject: !!e.projectId
      })));
    }
  }, [entries]);

  // AI-powered anomaly detection
  // Uses organization settings from database (live mode) or localStorage (demo mode)
  const aiWarnings = useMemo(() => {
    const warnings = aiService.detectAnomalies(
      entries, 
      currentWeekStart, 
      timesheets,
      currentEmployee?.workingPattern,
      organizationSettings
    );
    return warnings.filter(w => !dismissedWarnings.has(w.id));
  }, [entries, currentWeekStart, timesheets, currentEmployee, dismissedWarnings, organizationSettings]);

  // Calculate AI Insights badge counts
  const aiInsightsCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    aiWarnings.forEach(warning => {
      if (warning.severity === 'high') counts.high++;
      else if (warning.severity === 'medium') counts.medium++;
      else if (warning.severity === 'low') counts.low++;
    });
    return counts;
  }, [aiWarnings]);

  const enableCopyPreviousWeek = organizationSettings?.deadline?.enableCopyPreviousWeek ?? true;
  const enableAutoSmartFill = organizationSettings?.deadline?.enableAutoSmartFill ?? true;
  const enableAiInsights = organizationSettings?.deadline?.enableAiInsights ?? true;

    // AI-powered auto-fill suggestions
    const autoFillSuggestions = useMemo(() => {
      return aiService.generateAutoFillSuggestions(
        targetEmployeeId,
        timesheets,
        currentWeekStart
      );
    }, [targetEmployeeId, timesheets, currentWeekStart]);

  const VISIBLE_DAYS = useMemo(() => {
    const workingDays = organizationSettings?.workingProfile?.workingDays;
    if (!workingDays) return DAYS.filter(d => !isWeekendDay(d));
    return DAYS.filter(d => workingDays[d]?.isWorkingDay);
  }, [DAYS, organizationSettings?.workingProfile?.workingDays]);

  // Calculate totals
  const rowTotals = useMemo(() => {
    return entries.map(entry => 
      VISIBLE_DAYS.reduce((sum, day) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, VISIBLE_DAYS]);

  const columnTotals = useMemo(() => {
    return VISIBLE_DAYS.map(day => 
      entries.reduce((sum, entry) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, VISIBLE_DAYS]);

  const weekTotal = useMemo(() => 
    columnTotals.reduce((sum, total) => sum + total, 0)
  , [columnTotals]);

    // Check for missing descriptions (used for submission validation)
    const hasMissingDescriptions = useMemo(() => {
      return entries.some(entry => !entry.description.trim());
    }, [entries]);

    // Count entries with empty descriptions for bulk fill feature
    const emptyDescriptionCount = useMemo(() => {
      return entries.filter(entry => !entry.description.trim()).length;
    }, [entries]);

    // Handler for filling empty descriptions in bulk
    const handleFillEmptyDescriptions = () => {
      if (!bulkDescription.trim()) {
        toast.error('Please enter a description');
        return;
      }
    
      const updatedEntries = entries.map(entry => {
        if (!entry.description.trim()) {
          return { ...entry, description: bulkDescription.trim() };
        }
        return entry;
      });
    
      setEntries(updatedEntries);
      setShowFillDescriptionDialog(false);
      setBulkDescription('');
      toast.success(`Updated ${emptyDescriptionCount} description${emptyDescriptionCount !== 1 ? 's' : ''}`);
    };

    const addRow = () => {
    const newEntry: TimesheetEntry = {
      id: -Date.now(),
      projectId: '',
      costCentreId: '',
      description: '',
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      }
    };
    console.log('🆕 Adding new row:', newEntry);
    setEntries([...entries, newEntry]);
    console.log('📊 Total entries after add:', entries.length + 1);
  };

// State for delete confirmation dialog
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  });

  const deleteRow= (index: number) => {
    // Show confirmation dialog
    setDeleteConfirmation({ open: true, index });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.index !== null) {
      setEntries(entries.filter((_, i) => i !== deleteConfirmation.index));
      toast.success('Row deleted');
      setDeleteConfirmation({ open: false, index: null });
    }
  };

  const duplicateRow = (index: number) => {
    const entry = entries[index];
    const newEntry: TimesheetEntry = {
      ...entry,
      id: -Date.now(),
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
      // Remove scheduled booking flags for duplicated entries
      isFromSchedule: false,
      scheduledBookingId: undefined,
    };
    setEntries([...entries.slice(0, index + 1), newEntry, ...entries.slice(index + 1)]);
    toast.success('Row duplicated');
    
    // Focus on the new row after a short delay to allow DOM to update
    setTimeout(() => {
      // Find the first input in the new row (project selector)
      const newRowIndex = index + 1;
      const projectInput = document.querySelector(`[data-row-index="${newRowIndex}"] input`);
      if (projectInput instanceof HTMLElement) {
        projectInput.focus();
      }
    }, 100);
  };

  // Split row handlers
  const handleSplitRow = (entry: TimesheetEntry) => {
    setEntryToSplit(entry);
    setShowSplitRowSheet(true);
  };

  const handleSplitConfirm = (originalEntry: TimesheetEntry, newEntry: Partial<TimesheetEntry>, splitRatio: number) => {
    // Calculate the split hours for the original entry (remaining hours)
    const updatedOriginalHours = {
      monday: Math.round((originalEntry.hours.monday * (1 - splitRatio)) * 100) / 100,
      tuesday: Math.round((originalEntry.hours.tuesday * (1 - splitRatio)) * 100) / 100,
      wednesday: Math.round((originalEntry.hours.wednesday * (1 - splitRatio)) * 100) / 100,
      thursday: Math.round((originalEntry.hours.thursday * (1 - splitRatio)) * 100) / 100,
      friday: Math.round((originalEntry.hours.friday * (1 - splitRatio)) * 100) / 100,
      saturday: Math.round((originalEntry.hours.saturday * (1 - splitRatio)) * 100) / 100,
      sunday: Math.round((originalEntry.hours.sunday * (1 - splitRatio)) * 100) / 100,
    };

    // Create the new entry with a unique ID
    const completeNewEntry: TimesheetEntry = {
      id: -Date.now() - Math.random(),
      projectId: newEntry.projectId!,
      costCentreId: newEntry.costCentreId!,
      roleId: originalEntry.roleId,
      description: newEntry.description || '',
      hours: newEntry.hours!,
      // Copy relevant fields from original entry
      isFromSchedule: false,
      isDismissed: false,
      isReadOnly: false,
      isNonProjectTime: originalEntry.isNonProjectTime,
      // Remove scheduled booking flags for split entries
      scheduledBookingId: undefined,
      // Include split tracking metadata from newEntry
      splitFromEntryId: newEntry.splitFromEntryId,
      splitRatio: newEntry.splitRatio,
      splitAt: newEntry.splitAt,
    };

    // Update the original entry with remaining hours
    const updatedOriginalEntry = {
      ...originalEntry,
      hours: updatedOriginalHours,
    };

    // Update entries: replace original with updated version and add new entry
    setEntries(prevEntries => {
      const index = prevEntries.findIndex(e => e.id === originalEntry.id);
      if (index === -1) return prevEntries;

      const updated = [...prevEntries];
      updated[index] = updatedOriginalEntry;
      updated.splice(index + 1, 0, completeNewEntry); // Insert new entry right after original

      return updated;
    });

    toast.success('Row split successfully');
  };

  const updateEntry = (index: number, field: keyof TimesheetEntry, value: any) => {
    console.log('🔄 Updating entry:', { index, field, value, totalEntries: entries.length });
    
    setEntries(prevEntries => {
      if (index < 0 || index >= prevEntries.length) {
        console.error('❌ Invalid index:', index, 'Total entries:', prevEntries.length);
        toast.error('Error updating entry: Invalid row index');
        return prevEntries;
      }
      
      const updated = [...prevEntries];
      updated[index] = { ...updated[index], [field]: value };
      console.log('✅ Updated entry:', updated[index]);
      
      // Show success feedback for project selection
      if (field === 'projectId' && value) {
        const project = allAvailableProjects.find(p => p.id === value);
        if (project) {
          console.log('✅ Project selected:', project.name);
          toast.success(`Selected: ${project.name}`);
        }
      }
      
      return updated;
    });
  };

  const updateHours = (index: number, day: typeof DAYS[number], value: string) => {
    const hours = value === '' ? 0 : parseFloat(value);
    const final = isNaN(hours) ? 0 : hours;
    const updated = [...entries];
    updated[index] = {
      ...updated[index],
      hours: { ...updated[index].hours, [day]: final }
    };
    setEntries(updated);
  };

  const copyFromLastWeek = () => {
    const lastWeek = subWeeks(currentWeekStart, 1);
    const lastWeekEnding = addDays(lastWeek, 6);
    const lastTimesheet = timesheets.find(
      ts => ts.employeeId === targetEmployeeId && 
      format(ts.weekEnding, 'yyyy-MM-dd') === format(lastWeekEnding, 'yyyy-MM-dd')
    );

    if (lastTimesheet && lastTimesheet.entries.length > 0) {
      let timesheetNativeEntries = lastTimesheet.entries.filter(entry => {
        if (entry.isFromSchedule) return false;
        if (entry.sourceIntegration) return false;
        if (entry.absenceType) return false;
        return true;
      });

      const shouldCopyOnlyBillable = organizationSettings?.deadline?.copyOnlyBillable ?? false;
      if (shouldCopyOnlyBillable) {
        timesheetNativeEntries = timesheetNativeEntries.filter(entry => {
          const proj = allAvailableProjects.find(p => p.id === entry.projectId);
          return proj?.billable;
        });
      }

      const shouldCopyDescriptions = organizationSettings?.deadline?.copyDescriptions ?? true;

      if (timesheetNativeEntries.length > 0) {
        setEntries(timesheetNativeEntries.map((entry, index) => ({
          ...entry,
          id: -Date.now() - index,
          description: shouldCopyDescriptions ? entry.description : '',
          hours: {
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0
          }
        })));
        toast.success(`Copied ${timesheetNativeEntries.length} timesheet ${timesheetNativeEntries.length === 1 ? 'entry' : 'entries'} from last week`);
      } else {
        toast.error('No timesheet entries found to copy (absences and scheduled bookings are excluded)');
      }
    } else {
      toast.error('No timesheet found for last week');
    }
  };

  const saveTimesheet = () => {
    const updated = {
      ...currentTimesheet,
      entries,
      status: 'draft' as const
    };
    updateTimesheet(updated);
    toast.success('Timesheet saved as draft');
  };

  const submitTimesheet = async () => {
    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

        if (organizationSettings?.requireDescription === 'always' && hasMissingDescriptions) {
          toast.error('Please add descriptions to all entries');
          return;
        }

    // Validate project and cost centre requirements
    const settings = screenMode === 'live' 
      ? getCachedLiveOrgSettings() 
      : await getOrganisationTimesheetSettings(screenMode);
    
    const missingProjects = entries.filter(entry => !entry.projectId);
    if (missingProjects.length > 0) {
      toast.error('Cannot submit timesheet', {
        description: 'All entries must have a project assigned. Please add projects to all entries.',
        duration: 5000,
      });
      return;
    }

    // Only validate cost centre if it's required in settings
    if (settings?.requireCostCentre) {
      const missingCostCentres = entries.filter(entry => !entry.costCentreId);
      if (missingCostCentres.length > 0) {
        toast.error('Cannot submit timesheet', {
          description: 'All entries must have a cost centre assigned. Please add cost centres to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    // Check minimum hours requirement based on employee's working pattern
    const expectedWeeklyHours = currentEmployee?.workingPattern?.hoursPerWeek || 40;
    const totalHours = entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
    
    const minimumHoursThreshold = expectedWeeklyHours * 0.8; // Allow 20% tolerance
    
    if (totalHours < minimumHoursThreshold) {
      toast.error(
        `Insufficient hours logged`,
        {
          description: `You have logged ${totalHours.toFixed(1)}h but need at least ${minimumHoursThreshold.toFixed(1)}h (${expectedWeeklyHours}h expected). Please add missing time entries.`,
          duration: 6000,
        }
      );
      return;
    }

    // Show loading toast - declared outside try block so it can be dismissed in catch
    const loadingToast = toast.loading('Submitting timesheet...');

    try {

      if (screenMode === 'live') {
        // LIVE MODE: Use backend API to trigger workflow engine
        console.log('🔄 Submitting timesheet in LIVE mode via backend API...');
        
        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };
        
        // Check if this is a new timesheet that needs to be created first
        const isNewTimesheet = currentTimesheet.id.startsWith('ts-new-');
        let timesheetId = currentTimesheet.id;
        
        if (isNewTimesheet) {
          console.log('📝 Creating new timesheet before submission...');
          const createdTimesheet = await timesheetApi.createTimesheet({
            ...timesheetToSubmit,
            id: '',
            status: 'draft',
          });
          timesheetId = createdTimesheet.id;
          console.log(`✅ Created timesheet with ID: ${timesheetId}`);
        } else {
          // Update existing timesheet with current entries before submitting
          console.log('📝 Updating timesheet entries before submission...');
          await timesheetApi.updateTimesheet(timesheetId, {
            ...timesheetToSubmit,
            status: 'draft', // Keep as draft, submit endpoint will change status
          });
        }
        
        // Now call the submit endpoint to trigger the workflow engine
        console.log(`🚀 Calling submit API for timesheet ${timesheetId}...`);
        const submitResponse = await approvalsApi.submitTimesheet(timesheetId);
        
        // Dismiss loading toast
        toast.dismiss(loadingToast);
        
        if (submitResponse.message) {
          toast.success(submitResponse.message, {
            description: submitResponse.workflowInstanceId 
              ? 'Workflow started successfully' 
              : undefined,
            duration: 4000,
          });
        } else {
          toast.success('Timesheet submitted for approval');
        }
        
        // Fetch the updated timesheet from the server to get the latest state
        const updatedTimesheet = await timesheetApi.getTimesheetById(timesheetId);
        if (updatedTimesheet) {
          updateTimesheet(updatedTimesheet);
        }
        
        console.log('✅ Timesheet submitted successfully via backend workflow engine');
      } else {
        // DEMO MODE: Use client-side workflow simulation (existing behavior)
        console.log('🔄 Submitting timesheet in DEMO mode via client-side workflow...');
        
        // Import the submission service dynamically
        const { submitTimesheetWithWorkflow } = await import('@/services/timesheetSubmissionService');

        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };

        // Submit with full workflow integration (client-side)
        const result = await submitTimesheetWithWorkflow(
          timesheetToSubmit,
          currentEmployee,
          employees,
          timesheets
        );

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        if (result.success) {
          // Update the timesheet in context
          updateTimesheet(result.timesheet);

          // Show success message with details
          if (result.autoApproved) {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 5000,
            });
          } else {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 4000,
            });
          }

          // Log applied automation rules if any
          if (result.appliedRules && result.appliedRules.length > 0) {
            console.log('✅ Applied automation rules:', result.appliedRules);
          }
        } else {
          toast.error('Failed to submit timesheet');
        }
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error submitting timesheet:', error);
      toast.error('Failed to submit timesheet. Please try again.');
    }
  };

  const canNavigatePrev = useMemo(() => {
    const limit = organizationSettings?.historicalEntryLimit ?? 4;
    const earliest = subWeeks(startOfWeek(new Date(), { weekStartsOn: weekStartDay }), limit);
    return subWeeks(currentWeekStart, 1) >= earliest;
  }, [currentWeekStart, organizationSettings?.historicalEntryLimit, weekStartDay]);

  const canNavigateNext = useMemo(() => {
    const setting = organizationSettings?.allowFutureDatedEntries ?? '2weeks';
    if (setting === 'unlimited') return true;
    const now = startOfWeek(new Date(), { weekStartsOn: weekStartDay });
    if (setting === 'no') return addWeeks(currentWeekStart, 1) <= now;
    const weeksAhead = setting === '1week' ? 1 : setting === '2weeks' ? 2 : 4;
    return addWeeks(currentWeekStart, 1) <= addWeeks(now, weeksAhead);
  }, [currentWeekStart, organizationSettings?.allowFutureDatedEntries, weekStartDay]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && !canNavigatePrev) return;
    if (direction === 'next' && !canNavigateNext) return;
    setCurrentWeekStart(prev => 
      direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1)
    );
  };

  // AI Feature Handlers
  const handleDismissWarning = (warningId: string) => {
    setDismissedWarnings(prev => new Set([...prev, warningId]));
  };

  const handleApplyAutoFill = (suggestions: any[]) => {
    const newEntries = suggestions.map((suggestion, index) => ({
      id: -Date.now() - index,
      projectId: suggestion.projectId,
      costCentreId: suggestion.costCentreId,
      description: suggestion.description,
      hours: suggestion.hours,
    }));
    setEntries([...entries, ...newEntries]);
    toast.success(`Added ${suggestions.length} entries from AI suggestions`);
  };

  const handleNaturalLanguageEntry = (entry: Partial<TimesheetEntry>) => {
    const newEntry: TimesheetEntry = {
      id: -Date.now(),
      projectId: entry.projectId || '',
      costCentreId: entry.costCentreId || '',
      description: entry.description || '',
      hours: entry.hours || {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      },
    };
    setEntries([...entries, newEntry]);
  };

  // Scheduled booking handlers
  const handleDismissBooking = (index: number, reason: string, customReason?: string) => {
    const entry = entries[index];
    const dismissedEntry = dismissBooking(entry, customReason || reason);
    
    const updated = [...entries];
    updated[index] = dismissedEntry;
    setEntries(updated);
    
    toast.success('Booking dismissed', {
      description: 'You can still add hours manually or delete this row',
    });
  };

  const handleRestoreBooking = (index: number) => {
    const entry = entries[index];
    const restoredEntry = restoreBooking(entry);
    
    const updated = [...entries];
    updated[index] = restoredEntry;
    setEntries(updated);
    
    toast.success('Booking restored', {
      description: 'Original scheduled hours have been restored',
    });
  };

  // Get rejection history for current timesheet
  const rejectionHistory = useMemo(() => {
    if (!currentTimesheet.id || currentTimesheet.id.startsWith('ts-new-')) {
      return null;
    }
    return rejectionService.getTimesheetRejectionHistory(currentTimesheet.id);
  }, [currentTimesheet.id]);

  // Get the most recent pending rejection
  const pendingRejection = useMemo(() => {
    if (!rejectionHistory || rejectionHistory.rejections.length === 0) {
      return null;
    }
    return rejectionHistory.rejections.find(r => r.status === 'pending_correction') || null;
  }, [rejectionHistory]);

  // State for rejection details expansion
  const [showRejectionDetails, setShowRejectionDetails] = useState(true);

  // Handle resubmit
  const handleResubmit = () => {
    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

        if (organizationSettings?.requireDescription === 'always' && hasMissingDescriptions) {
          toast.error('Please add descriptions to all entries');
          return;
        }

    // Update rejection status to resubmitted
    if (pendingRejection) {
      rejectionService.updateRejectionStatus(pendingRejection.id, 'resubmitted');
    }

    const updated = {
      ...currentTimesheet,
      entries,
      status: 'submitted' as const,
      submittedAt: new Date()
    };
    updateTimesheet(updated);
    toast.success('Timesheet resubmitted for approval');
  };

  const isReadOnly = currentTimesheet.status !== 'draft' && currentTimesheet.status !== 'rejected';
  const isRejected = currentTimesheet.status === 'rejected' && pendingRejection;
  
  // Helper function to format status for display
  const getFormattedStatus = () => {
    return currentTimesheet.status.charAt(0).toUpperCase() + currentTimesheet.status.slice(1);
  };

  // Auto-load scheduled bookings when timesheet is opened
  // For draft/rejected timesheets: load and merge scheduled bookings for editing
  // For submitted/approved timesheets: load scheduled bookings for display (read-only)
  React.useEffect(() => {
    if (!hasLoadedScheduledBookings && entries.length >= 0) {
      const weekEnding = addDays(currentWeekStart, 6);
      
      // Use async API call in live mode, sync mock data in demo mode
      if (screenMode === 'live') {
        autoLoadScheduledBookingsAsync(targetEmployeeId, weekEnding, entries)
          .then(entriesWithScheduled => {
            const addedCount = entriesWithScheduled.length - entries.length;
            if (addedCount > 0) {
              setEntries(entriesWithScheduled);
              // Only show toast for editable timesheets
              if (!isReadOnly) {
                toast.success(`${addedCount} scheduled booking${addedCount > 1 ? 's' : ''} loaded from database`, {
                  description: 'Review and edit hours as needed',
                });
              }
            }
            setHasLoadedScheduledBookings(true);
          })
          .catch(error => {
            console.error('Failed to load scheduled bookings:', error);
            setHasLoadedScheduledBookings(true);
          });
      } else {
                const entriesWithScheduled = autoLoadScheduledBookings(
                  targetEmployeeId,
                  weekEnding,
                  entries
                );
        
        // Only update if we actually added scheduled bookings
        const addedCount = entriesWithScheduled.length - entries.length;
        if (addedCount > 0) {
          setEntries(entriesWithScheduled);
          // Only show toast for editable timesheets
          if (!isReadOnly) {
            toast.success(`${addedCount} scheduled booking${addedCount > 1 ? 's' : ''} loaded`, {
              description: 'Review and edit hours as needed',
            });
          }
        }
        
        setHasLoadedScheduledBookings(true);
      }
    }
  }, [isReadOnly, hasLoadedScheduledBookings, entries.length, currentWeekStart, targetEmployeeId, screenMode]);

  return (
    <TooltipProvider>
      <div className="space-y-2 sm:space-y-3">
      {/* Page Header */}
      <PageHeader
        title="Timesheets"
        subtitle="Log your time and manage your weekly timesheets"
        size={36}
        showActions={true}
        showBreadcrumb={false}
        actions={
          currentTimesheet.id && !currentTimesheet.id.startsWith('ts-new-') ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowAuditHistory(true)}
              aria-label="View History"
            >
              <History className="h-4 w-4" />
            </Button>
          ) : undefined
        }
      />

      {/* Mode Indicator Banner */}
      {/* <ModeIndicatorBanner
        mode={mode}
        screenName="My Timesheet"
        onNavigateToSettings={() => {
          // Navigate to settings - you can implement this based on your routing
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      /> */}

      {/* Rejection Status Banner - Prominent Display */}
      {isRejected && pendingRejection && (
        <Alert className="border-2 border-red-500 bg-red-50 dark:bg-red-950/20">
          <div className="flex items-start gap-3">
            <XCircle className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <AlertTitle className="text-red-900 dark:text-red-100 text-lg font-bold flex items-center justify-between">
                <span>Timesheet Rejected - Action Required</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRejectionDetails(!showRejectionDetails)}
                  className="h-8 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
                >
                  {showRejectionDetails ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Show Details
                    </>
                  )}
                </Button>
              </AlertTitle>
              
              <AlertDescription className="text-red-800 dark:text-red-200 space-y-3">
                {/* Rejection Summary */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                  <span className="font-medium">Rejected by:</span>
                  <span>{pendingRejection.approverName}</span>
                  <span className="hidden sm:inline text-red-600 dark:text-red-400">•</span>
                  <span className="font-medium">Date:</span>
                  <span>{format(pendingRejection.rejectionDate, 'EEEE, MMMM d, yyyy \'at\' h:mm a')}</span>
                </div>

                {/* Rejection Reason - Always Visible */}
                <div className="bg-red-100 dark:bg-red-900/40 border-2 border-red-300 dark:border-red-700 p-4 rounded-lg">
                  <div className="font-bold text-red-900 dark:text-red-100 mb-2 text-base">
                    📋 Rejection Reason:
                  </div>
                  <div className="text-red-900 dark:text-red-100 whitespace-pre-wrap">
                    {pendingRejection.rejectionReason}
                  </div>
                </div>

                {/* Expandable Details */}
                {showRejectionDetails && (
                  <div className="space-y-3 pt-2 border-t border-red-300 dark:border-red-700">
                    {/* Affected Entries */}
                    {pendingRejection.affectedEntries.length > 0 && (
                      <div>
                        <div className="font-semibold text-red-900 dark:text-red-100 mb-2">
                          Affected Entries ({pendingRejection.affectedEntries.length}):
                        </div>
                        <div className="space-y-2">
                          {pendingRejection.affectedEntries.map((entry, index) => (
                            <div 
                              key={index}
                              className="bg-white dark:bg-gray-900 border border-red-300 dark:border-red-700 p-3 rounded text-sm"
                            >
                              <div className="font-medium text-red-900 dark:text-red-100 mb-1">
                                {entry.projectName}
                              </div>
                              <div className="text-muted-foreground text-xs mb-1">
                                Cost Centre: {entry.costCentreName}
                              </div>
                              {entry.description && (
                                <div className="text-muted-foreground text-xs mb-2">
                                  Description: {entry.description}
                                </div>
                              )}
                              <div className="flex gap-2 flex-wrap">
                                {Object.entries(entry.hours).map(([day, hours]) => 
                                  hours > 0 ? (
                                    <span key={day} className="text-xs bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-100 px-2 py-1 rounded">
                                      {day.substring(0, 3)}: {hours}h
                                    </span>
                                  ) : null
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Instructions */}
                    <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-700 p-3 rounded">
                      <div className="font-semibold text-orange-900 dark:text-orange-100 mb-1 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        What to do next:
                      </div>
                      <ol className="list-decimal list-inside text-sm text-orange-800 dark:text-orange-200 space-y-1">
                        <li>Review the rejection reason above</li>
                        <li>Make necessary corrections to your timesheet entries</li>
                        <li>Click "Resubmit for Approval" when ready</li>
                      </ol>
                    </div>
                  </div>
                )}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* Zone 1: Data Entry Toolbar - Redesigned Single Row Layout */}
      <div className="pb-2">
        <Card className="border-primary/20 bg-primary/5 shadow-md">
          <CardContent className="p-3 sm:p-4">
            {/* Desktop Layout (≥1024px) - Simplified Single Row */}
            <div className="hidden lg:flex items-center justify-between gap-3 w-full">
              {/* Week Selector */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('prev')}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2 min-w-[280px]">
                  <h2 className="text-base font-semibold">
                    Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
                  </h2>
                  <Badge variant={
                    currentTimesheet.status === 'draft' ? 'secondary' :
                    currentTimesheet.status === 'submitted' ? 'default' :
                    currentTimesheet.status === 'approved' ? 'default' :
                    'secondary'
                  } className="text-xs w-[90px] justify-center">
                    {currentTimesheet.status.toUpperCase()}
                  </Badge>
                </div>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('next')}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>


            </div>

            {/* Tablet Layout (768px - 1023px) - Compact Single Row */}
            <div className="hidden md:flex lg:hidden items-center justify-between gap-2 w-full">
              {/* Week Selector */}
              <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('prev')}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-2 min-w-[240px]">
                    <h2 className="text-sm font-semibold">
                      Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
                    </h2>
                    <Badge variant={
                      currentTimesheet.status === 'draft' ? 'secondary' :
                      currentTimesheet.status === 'submitted' ? 'default' :
                      currentTimesheet.status === 'approved' ? 'default' :
                      'secondary'
                    } className="text-xs w-[90px] justify-center">
                      {currentTimesheet.status.toUpperCase()}
                    </Badge>
                  </div>
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('next')}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>


            </div>

            {/* Mobile Layout (<768px) - Simplified */}
            <div className="flex md:hidden flex-col gap-3">
              {/* Week Selector */}
              <div className="flex items-center gap-2 justify-center">
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('prev')}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-2 min-w-[200px] justify-center">
                    <h2 className="text-sm font-semibold">
                      Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
                    </h2>
                    <Badge variant={
                      currentTimesheet.status === 'draft' ? 'secondary' :
                      currentTimesheet.status === 'submitted' ? 'default' :
                      currentTimesheet.status === 'approved' ? 'default' :
                      'secondary'
                    } className="text-xs w-[90px] justify-center">
                      {currentTimesheet.status.toUpperCase()}
                    </Badge>
                  </div>
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('next')}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>


            </div>
          </CardContent>
        </Card>
      </div>

      {/* Smart Auto-Fill Dialog */}
      <SmartAutoFillDialog
        open={showAutoFillDialog}
        onOpenChange={setShowAutoFillDialog}
        suggestions={autoFillSuggestions}
        projects={projects}
        onApply={handleApplyAutoFill}
      />

      {/* Grid */}
      <Card data-tour="timesheet-grid">
        <CardContent className="p-0">
          {/* Status Legend */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 px-4 pt-3 pb-2 text-xs text-muted-foreground border-b bg-muted/30">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 rounded-full" style={{ backgroundColor: 'var(--success-400)' }} aria-hidden="true" />
              <span>Billable</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 border-l-4 border-double" style={{ borderLeftColor: 'var(--info-400)' }} aria-hidden="true" />
              <span>Scheduled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 border-l-4 border-dotted" style={{ borderLeftColor: 'var(--electricviolet-400)' }} aria-hidden="true" />
              <span>Absence</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[180px] sm:w-[250px]">Project</th>
                  {organizationSettings?.showRole && (
                    <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[120px] sm:w-[140px]">
                      Role{organizationSettings?.requireRole && <span className="text-destructive ml-0.5">*</span>}
                    </th>
                  )}
                  {organizationSettings?.showCostCentre !== false && (
                    <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[120px] sm:w-[140px]">
                      Cost Centre{organizationSettings?.requireCostCentre && <span className="text-destructive ml-0.5">*</span>}
                    </th>
                  )}
                  <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[150px] sm:w-[180px]">Description</th>
                  {VISIBLE_DAYS.map((day, i) => (
                    <th key={day} className={`p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[60px] sm:w-[70px] ${isWeekendDay(day) ? 'bg-muted' : ''}`}>
                      {format(addDays(currentWeekStart, i), 'EEE')}
                      <div className="text-[10px] sm:text-xs font-normal text-muted-foreground">
                        {format(addDays(currentWeekStart, i), 'dd/MM')}
                      </div>
                    </th>
                  ))}
                  <th className="p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[60px] sm:w-[70px]">Total</th>
                  <th className="p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[70px] sm:w-[90px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => {
                  const project = allAvailableProjects.find(p => p.id === entry.projectId);
                  // Get cost centres filtered by project code (if available)
                  // In live mode, these are fetched from the API filtered by project code
                  // In demo mode, all cost centres are used (no filtering)
                  const projectCostCentres = project?.code 
                    ? (costCentresByProjectCode[project.code] || [])
                    : [];
                  // Merge with enriched global cost centres for favourites/recently used
                  const enrichedCostCentres = projectCostCentres.map(cc => {
                    const enriched = costCentres.find(ecc => ecc.id === cc.id);
                    return enriched || cc;
                  });
                  // Check if cost centres are still loading for this project
                  const isLoadingCostCentres = project?.code 
                    ? loadingCostCentresForProject[project.code] || false
                    : false;
                  
                  // Check if this is a scheduled entry
                  const isScheduled = entry.isFromSchedule || false;
                  const isDismissed = entry.isDismissed || false;
                  const isEdited = isScheduled && !isDismissed && hasEditedHours(entry);
                  
                  // Check if this is an absence entry from HR system
                  const isAbsence = entry.sourceIntegration === 'people-hr';
                  
                  // Debug logging - log ALL entries to see what we have
                  console.log('[TimesheetGrid] Entry:', {
                    id: entry.id,
                    description: entry.description,
                    sourceIntegration: entry.sourceIntegration,
                    isAbsence,
                    isReadOnly: entry.isReadOnly,
                    isNonProjectTime: entry.isNonProjectTime
                  });
                  const isAbsenceReadOnly = isAbsence && entry.isReadOnly;
                  
                  // Extract absence type from entry.absenceType if available, otherwise try to parse from description
                  let absenceType: AbsenceType | null = null;
                  if (isAbsence) {
                    if (entry.absenceType) {
                      absenceType = entry.absenceType;
                    } else if (entry.description) {
                      // Fallback: try to parse from description
                      const parsed = entry.description.toLowerCase().replace(/\s+/g, '-');
                      console.log('[TimesheetGrid] Parsed absence type from description:', { description: entry.description, parsed });
                      
                      // Validate against known absence types
                      const validTypes: AbsenceType[] = [
                        'annual-leave',
                        'sick-leave',
                        'parental-leave',
                        'compassionate-leave',
                        'public-holiday',
                        'unpaid-leave',
                        'study-leave'
                      ];
                      
                      // Map 'training' to 'study-leave' for backwards compatibility
                      if (parsed === 'training' || parsed === 'professional-development-training') {
                        absenceType = 'study-leave';
                      } else if (validTypes.includes(parsed as AbsenceType)) {
                        absenceType = parsed as AbsenceType;
                      }
                    }
                  }
                  
                  // Determine status dot type
                  const statusType: StatusDotType = isDismissed 
                    ? 'dismissed' 
                    : isEdited 
                    ? 'edited' 
                    : 'scheduled';
                  
                  return (
                    <tr 
                      key={entry.id}
                      className={cn(
                        "border-b transition-colors hover:bg-muted/50",
                        // Absence entry styling (takes priority) - purple dotted left border
                        isAbsence && "bg-purple-50/30 hover:bg-purple-50/50 border-l-4 border-dotted",
                        // Scheduled entry styling (pale blue shade) - blue double left border
                        !isAbsence && isScheduled && "bg-blue-50/40 hover:bg-blue-50/60 border-l-4 border-double",
                        // Billable entry styling (green solid left accent) - only for non-scheduled, non-absence entries
                        !isAbsence && !isScheduled && project?.billable && "border-l-4"
                      )}
                      style={{
                        borderLeftColor: isAbsence 
                          ? 'var(--electricviolet-400)' 
                          : isScheduled 
                          ? 'var(--info-400)' 
                          : project?.billable 
                          ? 'var(--success-400)' 
                          : undefined
                      }}
                      aria-label={
                        isAbsence 
                          ? `Absence entry: ${entry.description}, read-only`
                          : project 
                          ? `${project.name}, ${project.billable ? 'billable' : 'non-billable'} project${isScheduled ? `, scheduled booking (${statusType})` : ', manual entry'}` 
                          : 'No project selected'
                      }
                    >
                      <td className="p-1 sm:p-1.5">
                        {/* Show project selector for absence entries */}
                        {isAbsence ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <ProjectSelector
                                projects={allAvailableProjects}
                                value={entry.projectId}
                                onChange={() => {}} // No-op for absence entries
                                disabled={true}
                                // Server-side pagination props
                                onLoadMore={loadMoreProjects}
                                hasMore={projectsHasMore}
                                remainingCount={projectsRemainingCount}
                                isLoadingMore={projectsLoading}
                                // Server-side search props
                                onServerSearch={handleProjectSearch}
                                isSearching={isSearchingProjects}
                                totalCount={projectsTotalCount}
                              />
                            </div>
                            {allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        ) : isScheduled ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <BookingHoverCard
                                booking={entry}
                                statusType={statusType}
                              >
                                <ProjectSelector
                                      projects={allAvailableProjects}
                                      value={entry.projectId}
                                      onChange={(projectId) => {
                                        updateEntry(index, 'projectId', projectId);
                                        // Clear cost centre when project changes - user must select a new one
                                        updateEntry(index, 'costCentreId', '');
                                        // Fetch filtered cost centres for the new project
                                        const proj = allAvailableProjects.find(p => p.id === projectId);
                                        if (proj?.code) {
                                          fetchCostCentresForProject(proj.code);
                                        }
                                      }}
                                      disabled={isReadOnly}
                                      // Server-side pagination props
                                      onLoadMore={loadMoreProjects}
                                      hasMore={projectsHasMore}
                                      remainingCount={projectsRemainingCount}
                                      isLoadingMore={projectsLoading}
                                      // Server-side search props
                                      onServerSearch={handleProjectSearch}
                                      isSearching={isSearchingProjects}
                                      totalCount={projectsTotalCount}
                                    />
                              </BookingHoverCard>
                            </div>
                            {allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <ProjectSelector
                                    projects={allAvailableProjects}
                                    value={entry.projectId}
                                    onChange={(projectId) => {
                                      updateEntry(index, 'projectId', projectId);
                                      // Clear cost centre when project changes - user must select a new one
                                      updateEntry(index, 'costCentreId', '');
                                      // Fetch filtered cost centres for the new project
                                      const proj = allAvailableProjects.find(p => p.id === projectId);
                                      if (proj?.code) {
                                        fetchCostCentresForProject(proj.code);
                                      }
                                    }}
                                    disabled={isReadOnly}
                                    // Server-side pagination props
                                    onLoadMore={loadMoreProjects}
                                    hasMore={projectsHasMore}
                                    remainingCount={projectsRemainingCount}
                                    isLoadingMore={projectsLoading}
                                    // Server-side search props
                                    onServerSearch={handleProjectSearch}
                                    isSearching={isSearchingProjects}
                                    totalCount={projectsTotalCount}
                                  />
                            </div>
                            {allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        )}
                      </td>
                      {organizationSettings?.showRole && (
                        <td className="p-1 sm:p-1.5">
                          <RoleSelector
                            value={entry.roleId}
                            onChange={(value) => {
                              if (!isAbsence) {
                                updateEntry(index, 'roleId', value);
                              }
                            }}
                            disabled={isAbsence || isReadOnly}
                            required={organizationSettings?.requireRole}
                          />
                        </td>
                      )}
                      {organizationSettings?.showCostCentre !== false && (
                        <td className="p-1 sm:p-1.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <CostCentreSelector
                                costCentres={enrichedCostCentres}
                                value={entry.costCentreId}
                                onChange={(value) => {
                                  if (!isAbsence) {
                                    updateEntry(index, 'costCentreId', value);
                                    updateCostCentreLastUsed(value);
                                  }
                                }}
                                disabled={isAbsence || isReadOnly || !project}
                                required={organizationSettings?.requireCostCentre}
                              />
                            </div>
                            {enrichedCostCentres.find(c => c.id === entry.costCentreId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        </td>
                      )}
                      <td className="p-1 sm:p-1.5">
                        {isAbsence ? (
                          <Textarea
                            value={entry.description}
                            onChange={() => {}} // No-op for absence entries
                            placeholder="Activity description..."
                            className="h-9 min-h-9 resize-none text-xs sm:text-sm bg-muted/50 cursor-not-allowed"
                            disabled={true}
                          />
                        ) : (
                          <Textarea
                            value={entry.description}
                            onChange={(e) => updateEntry(index, 'description', e.target.value)}
                            placeholder="Activity description..."
                            className="h-9 min-h-9 resize-none text-xs sm:text-sm"
                            disabled={isReadOnly}
                          />
                        )}
                      </td>
                      {VISIBLE_DAYS.map((day, i) => {
                        const cellKey = getHoursCellKey(entry.id, day);
                        const isEditing = editingHours[cellKey] !== undefined;
                        const displayValue = isEditing
                          ? editingHours[cellKey]!
                          : entry.hours[day] === 0
                            ? ''
                            : entry.hours[day].toFixed(2);
                        
                        return (
                          <td key={day} className={`p-1 ${isWeekendDay(day) ? 'bg-muted/30' : ''}`}>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={displayValue}
                              onChange={(e) => {
                                if (!isAbsence) {
                                  setEditingHours(prev => ({ ...prev, [cellKey]: e.target.value }));
                                }
                              }}
                              onFocus={(e) => {
                                // Select all text on focus for easy overwriting (Excel-like behavior)
                                e.target.select();
                              }}
                              onBlur={(e) => {
                                if (!isAbsence) {
                                  const raw = e.target.value;
                                  let hours = raw === '' ? 0 : parseFloat(raw);
                                  if (isNaN(hours)) hours = 0;
                                  // Apply rounding based on settings
                                  const timeRounding = organizationSettings?.timeRounding ?? 'none';
                                  let step = 0;
                                  if (timeRounding === '15min') step = 0.25;
                                  else if (timeRounding === '30min') step = 0.5;
                                  else if (timeRounding === '1hour') step = 1;
                                  if (step > 0) {
                                    hours = Math.round(hours / step) * step;
                                  }
                                  // Enforce minimum entry duration (if non-zero)
                                  const minSetting = organizationSettings?.minimumEntryDuration ?? '6min';
                                  let min = 0.1; // 6min
                                  if (minSetting === '15min') min = 0.25;
                                  else if (minSetting === '30min') min = 0.5;
                                  else if (minSetting === '1hour') min = 1;
                                  if (hours !== 0 && Math.abs(hours) < min) {
                                    hours = hours < 0 ? -min : min;
                                  }
                                  // Clamp based on allowNegativeHours
                                  const allowNeg = organizationSettings?.allowNegativeHours ?? false;
                                  const clamped = allowNeg ? Math.max(-24, Math.min(24, hours)) : Math.max(0, Math.min(24, hours));
                                  updateHours(index, day, String(clamped));
                                  setEditingHours(prev => {
                                    const next = { ...prev };
                                    delete next[cellKey];
                                    return next;
                                  });
                                }
                              }}
                              onKeyDown={(e) => {
                                // Allow keyboard navigation like Excel
                                if (e.key === 'Enter' || e.key === 'Tab') {
                                  e.currentTarget.blur();
                                }
                                // Allow arrow keys for increment/decrement
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const current = parseFloat(displayValue) || 0;
                                  const newValue = Math.min(24, current + 0.5);
                                  setEditingHours(prev => ({ ...prev, [cellKey]: newValue.toFixed(1) }));
                                }
                                if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const current = parseFloat(displayValue) || 0;
                                  const newValue = Math.max(0, current - 0.5);
                                  setEditingHours(prev => ({ ...prev, [cellKey]: newValue.toFixed(1) }));
                                }
                              }}
                              placeholder={entry.hours[day] === 0 ? '0' : ''}
                              className={cn(
                                "text-center text-sm h-9 px-1 sm:px-2",
                                "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                entry.hours[day] > 0 ? "text-foreground" : "text-muted-foreground/40",
                                (isReadOnly || isAbsence) && "bg-muted/50 cursor-not-allowed",
                                !isReadOnly && !isAbsence && "hover:border-primary/50 hover:bg-accent/30 transition-colors cursor-text",
                                isAbsence && entry.hours[day] > 0 && "bg-purple-50/40 border-purple-200"
                              )}
                              disabled={isReadOnly || isAbsence}
                            />
                          </td>
                        );
                      })}
                      <td className={cn(
                        "p-1 sm:p-1.5 text-center font-bold text-sm",
                        rowTotals[index] > 0 ? "text-foreground bg-muted/30" : "text-muted-foreground/50"
                      )}>
                        {rowTotals[index].toFixed(2)}
                      </td>
                      <td className="p-1 sm:p-1.5">
                        <div className="flex items-center justify-center gap-0.5 sm:gap-1">
                          {/* No actions for absence entries */}
                          {isAbsence ? (
                            <AbsenceInlineActions
                              onViewDetails={() => setSelectedAbsenceForDetails(entry)}
                            />
                          ) : (
                            <>
                              {/* Scheduled Booking Inline Actions */}
                              {isScheduled && (
                                <BookingInlineActions
                                  booking={entry}
                                  onDuplicate={() => duplicateRow(index)}
                                  onDelete={() => deleteRow(index)}
                                  onSplit={() => handleSplitRow(entry)}
                                  onViewDetails={() => setSelectedBookingForDetails(entry)}
                                  disabled={isReadOnly}
                                  canSplit={rowTotals[index] > 0}
                                  readOnlyMode={isReadOnly}
                                />
                              )}
                              
                              {/* Manual Entry Inline Actions */}
                              {!isScheduled && !isReadOnly && (
                                <ManualEntryInlineActions
                                  onDuplicate={() => duplicateRow(index)}
                                  onDelete={() => deleteRow(index)}
                                  onSplit={() => handleSplitRow(entry)}
                                  disabled={isReadOnly}
                                  canSplit={rowTotals[index] > 0}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-muted/70 font-bold">
                  <td className="p-2 sm:p-2.5">
                    {!isReadOnly && (
                      <div className="flex gap-2 items-center">
                        <Button 
                          onClick={addRow} 
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          title="Add a new timesheet row (Alt+A)"
                        >
                          <Plus className="h-4 w-4" />
                          Add Row
                        </Button>
                        {enableCopyPreviousWeek && (
                          <Button
                            onClick={copyFromLastWeek}
                            variant="outline"
                            size="sm"
                            className="gap-2"
                          >
                            <Copy className="h-4 w-4" />
                            Copy Last Week
                          </Button>
                        )}
                        {enableAutoSmartFill && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                onClick={() => setShowAutoFillDialog(true)}
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <Sparkles className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Smart Autofill</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </td>
                  <td 
                    colSpan={
                      (organizationSettings?.showRole ? 1 : 0) + 
                      (organizationSettings?.showCostCentre !== false ? 1 : 0) + 
                      1
                    } 
                    className="p-2 sm:p-2.5 text-right text-xs sm:text-sm"
                  >
                    Daily Totals:
                  </td>
                  {columnTotals.map((total, i) => (
                    <td key={i} className={cn(
                      "p-2 sm:p-2.5 text-center text-xs sm:text-sm",
                      isWeekendDay(VISIBLE_DAYS[i]) ? 'bg-muted' : '',
                      total > 0 && "text-foreground"
                    )}>
                      {total.toFixed(2)}
                    </td>
                  ))}
                  <td className="p-2 sm:p-2.5 text-center text-sm bg-primary/10 text-primary font-bold">
                    {weekTotal.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* AI Anomaly Warnings - Positioned near submit for final review */}
      {/* COMMENTED OUT: Replaced with AIInsightsSheet in sticky footer
      {aiWarnings.length > 0 && (
        <AnomalyAlertsPanelEnhanced
          warnings={aiWarnings}
          onDismiss={handleDismissWarning}
        />
      )}
      */}

      {/* Zone 3: Review & Submission Footer (Sticky) */}
      {(!isReadOnly || aiWarnings.length > 0) && (
        <div className="bg-background border-t-2 border-primary/20 shadow-md mt-4 rounded-lg">
          <Card className="border-0 rounded-lg">
            <CardContent className="p-3 sm:p-4">
              <div className={cn(
                "flex flex-col sm:flex-row items-center gap-3",
                isReadOnly && aiWarnings.length > 0 ? "justify-center" : "justify-end"
              )}>
                {/* Action Buttons */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {/* AI Insights Button */}
                  {enableAiInsights && aiWarnings.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="default"
                      onClick={() => setShowAIInsightsSheet(true)}
                      className="flex-1 sm:flex-none gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span>Insights</span>
                    </Button>
                  )}
                  
                  {/* Save/Submit Buttons - Only show when NOT readonly */}
                  {!isReadOnly && (
                    <>
                      <Button 
                        variant="outline" 
                        size="default"
                        onClick={saveTimesheet}
                        className="flex-1 sm:flex-none gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Save Draft
                      </Button>
                      {isRejected ? (
                        <Button 
                          size="default"
                          onClick={handleResubmit}
                          disabled={entries.length === 0 || hasMissingDescriptions}
                          className="flex-1 sm:flex-none gap-2 font-semibold bg-orange-600 hover:bg-orange-700"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Resubmit for Approval
                        </Button>
                      ) : (
                        <Button 
                          size="default"
                          onClick={submitTimesheet}
                          disabled={entries.length === 0 || hasMissingDescriptions}
                          className="flex-1 sm:flex-none gap-2 font-semibold"
                          data-tour="submit-timesheet"
                        >
                          <Send className="h-4 w-4" />
                          Submit for Approval
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Validation Messages - Only show when NOT readonly */}
              {!isReadOnly && (
                <>
                  {entries.length === 0 && (
                    <div className="mt-2 text-xs text-muted-foreground text-center">
                      Add at least one timesheet entry to submit
                    </div>
                  )}
                  {entries.length > 0 && hasMissingDescriptions && (
                    <div className="mt-2 text-xs text-destructive text-center">
                      Please add descriptions to all entries before submitting
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mobile FAB (Floating Action Button) - Only visible on mobile */}
      {!isReadOnly && (
        <Button
          onClick={addRow}
          size="lg"
          className="fixed bottom-20 right-4 z-30 h-14 w-14 rounded-full shadow-2xl md:hidden hover:scale-110 transition-transform"
          title="Add Row"
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      {/* Audit History Dialog */}
      <AuditHistoryDialog
        open={showAuditHistory}
        onOpenChange={setShowAuditHistory}
        timesheetId={currentTimesheet.id}
        employeeName={currentEmployee.name}
        weekEnding={currentTimesheet.weekEnding}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onOpenChange={(open) => setDeleteConfirmation({ open, index: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Row</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this timesheet entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmation({ open: false, index: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Row Sheet */}
      <SplitRowSheet
        open={showSplitRowSheet}
        onOpenChange={setShowSplitRowSheet}
        entry={entryToSplit}
        projects={projects}
        costCentres={costCentres}
        onSplit={handleSplitConfirm}
      />

      {/* Fill Empty Descriptions Dialog */}
      <Dialog open={showFillDescriptionDialog} onOpenChange={setShowFillDescriptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fill Empty Descriptions</DialogTitle>
            <DialogDescription>
              This will update {emptyDescriptionCount} row{emptyDescriptionCount !== 1 ? 's' : ''} with blank descriptions.
              Rows that already have descriptions will not be changed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={bulkDescription}
              onChange={(e) => setBulkDescription(e.target.value)}
              placeholder="Enter description for selected rows..."
              className="min-h-[100px]"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowFillDescriptionDialog(false);
                setBulkDescription('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleFillEmptyDescriptions}>
              Fill {emptyDescriptionCount} Description{emptyDescriptionCount !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Details Modal */}
      {selectedBookingForDetails && (
        <BookingDetailsModal
          open={!!selectedBookingForDetails}
          onOpenChange={(open) => !open && setSelectedBookingForDetails(null)}
          booking={{
            projectName: allAvailableProjects.find(p => p.id === selectedBookingForDetails.projectId)?.name || 'Unknown Project',
            costCentreName: costCentres.find(cc => cc.id === selectedBookingForDetails.costCentreId)?.name || 'Unknown Cost Centre',
            scheduledHours: selectedBookingForDetails.scheduledHours || {
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
            },
            bookingCreatedByName: selectedBookingForDetails.bookingCreatedByName,
            bookingCreatedAt: selectedBookingForDetails.bookingCreatedAt,
            bookingNotes: selectedBookingForDetails.bookingNotes,
          }}
          onDismiss={() => {
            const index = entries.findIndex(e => e.id === selectedBookingForDetails.id);
            if (index !== -1) {
              setSelectedBookingForDismiss(selectedBookingForDetails);
              setSelectedBookingForDetails(null);
            }
          }}
          readOnly={isReadOnly}
        />
      )}

      {/* Dismiss Booking Modal */}
      {selectedBookingForDismiss && (
        <DismissBookingModal
          open={!!selectedBookingForDismiss}
          onOpenChange={(open) => !open && setSelectedBookingForDismiss(null)}
          booking={{
            projectName: allAvailableProjects.find(p => p.id === selectedBookingForDismiss.projectId)?.name || 'Unknown Project',
            scheduledHours: selectedBookingForDismiss.scheduledHours || {
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
            },
          }}
          onConfirm={(reason, customReason) => {
            const index = entries.findIndex(e => e.id === selectedBookingForDismiss.id);
            if (index !== -1) {
              handleDismissBooking(index, reason, customReason);
            }
            setSelectedBookingForDismiss(null);
          }}
        />
      )}

      {/* Absence Details Modal */}
      {selectedAbsenceForDetails && (
        <AbsenceDetailsModal
          open={!!selectedAbsenceForDetails}
          onOpenChange={(open) => !open && setSelectedAbsenceForDetails(null)}
          entry={{
            absenceType: selectedAbsenceForDetails.absenceType || 'annual-leave',
            absenceReason: selectedAbsenceForDetails.absenceReason,
            approvedBy: selectedAbsenceForDetails.approvedBy,
            approvedAt: selectedAbsenceForDetails.approvedAt,
            absenceId: selectedAbsenceForDetails.absenceId,
            hours: selectedAbsenceForDetails.hours,
            sourceIntegration: selectedAbsenceForDetails.sourceIntegration,
          }}
        />
      )}

      {/* AI Insights Sheet */}
      <AIInsightsSheet
        open={showAIInsightsSheet}
        onOpenChange={setShowAIInsightsSheet}
        warnings={aiWarnings}
        onDismiss={handleDismissWarning}
        onAutoFix={undefined}
      />
      </div>
    </TooltipProvider>
  );
}
